import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-600" />
              <div>
                <CardTitle className="text-2xl">Privacy Policy</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  WorkSafeAgent LLC - Effective Date: December 14, 2025
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <div className="space-y-6">
              <section>
                <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
                <p>
                  This Privacy Policy describes how WorkSafeAgent LLC ("we," "us," or "our") collects, uses, and discloses 
                  information about you when you use our OSHA Safety Audit application and related services (collectively, 
                  the "Services"). By accessing or using our Services, you agree to this Privacy Policy.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">2.1 Information You Provide</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Account information (name, email address, password)</li>
                  <li>Company and organization details</li>
                  <li>Safety audit data, incident reports, and workplace observations</li>
                  <li>Training records and certifications</li>
                  <li>Communications with our support team</li>
                  <li>Payment information (processed by third-party payment processors)</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">2.2 Automatically Collected Information</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>Log data and error reports</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">3. How We Use Your Information</h2>
                <p>We use the information we collect to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide, maintain, and improve our Services</li>
                  <li>Process safety audits and generate reports</li>
                  <li>Provide AI-powered safety analysis and recommendations</li>
                  <li>Send notifications about safety issues and system updates</li>
                  <li>Process payments and manage subscriptions</li>
                  <li>Respond to your requests and provide customer support</li>
                  <li>Detect, prevent, and address technical issues and security threats</li>
                  <li>Comply with legal obligations and enforce our terms</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">4. Information Sharing and Disclosure</h2>
                <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (hosting, analytics, payment processing, AI services)</li>
                  <li><strong>Legal Requirements:</strong> When required by law, subpoena, or legal process</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
                  <li><strong>Emergency Situations:</strong> To protect the safety and rights of users or the public</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">5. Data Security</h2>
                <p>
                  We implement reasonable security measures to protect your information from unauthorized access, alteration, 
                  disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 
                  100% secure. We cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">6. Data Retention</h2>
                <p>
                  We retain your information for as long as necessary to provide the Services, comply with legal obligations, 
                  resolve disputes, and enforce our agreements. Safety audit records may be retained for extended periods 
                  as required by OSHA regulations and industry standards.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">7. Your Rights and Choices</h2>
                <p>Depending on your location, you may have certain rights regarding your personal information:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Access and obtain a copy of your personal information</li>
                  <li>Correct inaccurate or incomplete information</li>
                  <li>Request deletion of your information (subject to legal retention requirements)</li>
                  <li>Object to or restrict certain processing of your information</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Data portability (receive your data in a structured format)</li>
                </ul>
                <p className="mt-2">To exercise these rights, contact us at privacy@wsafeai.com</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">8. Third-Party Services</h2>
                <p>
                  Our Services may contain links to third-party websites or integrate with third-party services 
                  (including AI providers like OpenAI). We are not responsible for the privacy practices of these 
                  third parties. We encourage you to review their privacy policies.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">9. Children's Privacy</h2>
                <p>
                  Our Services are not intended for individuals under the age of 18. We do not knowingly collect 
                  personal information from children. If you believe we have collected information from a child, 
                  please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">10. International Data Transfers</h2>
                <p>
                  Your information may be transferred to and processed in countries other than your country of residence. 
                  These countries may have data protection laws different from your jurisdiction. By using our Services, 
                  you consent to such transfers.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">11. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of material changes by posting 
                  the updated policy on our Services and updating the "Effective Date" above. Your continued use of the 
                  Services after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">12. Contact Us</h2>
                <p>If you have questions or concerns about this Privacy Policy or our data practices, contact us at:</p>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">WorkSafeAgent LLC</p>
                  <p>Email: privacy@wsafeai.com</p>
                  <p>Website: www.wsafeai.com</p>
                </div>
              </section>

              <section className="border-t pt-6 mt-8">
                <p className="text-sm text-gray-600 italic">
                  © 2025-2026 WorkSafeAgent LLC. All rights reserved. This privacy policy is subject 
                  to change without notice. Please review it regularly to stay informed about how we protect your information.
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
